const DEFAULT_ALLOWED_HOSTS = ["*.businesscentral.dynamics.com"];
const ABSOLUTE_HTTP_URL_RE = /^(https?):\/\/([^/?#:]+)(?::(\d+))?([/?#].*)?$/i;

export interface BcEndpointValidationOptions {
  allowedHosts?: string[];
  allowInsecureHttp?: boolean;
}

export type BcEndpointValidationResult =
  | { ok: true; normalizedUrl: string; host: string }
  | { ok: false; error: string };

function parseAbsoluteHttpUrl(value: string): { protocol: string; host: string; port: string; remainder: string } | null {
  const match = ABSOLUTE_HTTP_URL_RE.exec(String(value ?? "").trim());
  if (!match) return null;
  return {
    protocol: `${match[1].toLowerCase()}:`,
    host: match[2].toLowerCase(),
    port: match[3] ?? "",
    remainder: match[4] ?? ""
  };
}

export function extractHostFromHttpUrl(value: string): string {
  const parsed = parseAbsoluteHttpUrl(value);
  if (!parsed) return "";
  if (parsed.host.includes("@")) return "";
  return parsed.host;
}

function normalizeAllowedHostEntry(value: string): string {
  const trimmed = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!trimmed) return "";

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return extractHostFromHttpUrl(trimmed);
  }

  return trimmed.replace(/\/+$/, "");
}

function hostMatchesAllowed(host: string, allowedEntry: string): boolean {
  if (!allowedEntry) return false;
  if (allowedEntry.startsWith("*.")) {
    const suffix = allowedEntry.slice(2);
    if (!suffix) return false;
    return host === suffix || host.endsWith(`.${suffix}`);
  }
  return host === allowedEntry;
}

export function validateBcApiEndpoint(
  rawEndpoint: string,
  options?: BcEndpointValidationOptions,
): BcEndpointValidationResult {
  const endpoint = String(rawEndpoint ?? "").trim();
  if (!endpoint) {
    return { ok: false, error: "BC API endpoint is not configured." };
  }

  const parsed = parseAbsoluteHttpUrl(endpoint);

  if (!parsed) {
    return { ok: false, error: "BC API endpoint must be an absolute URL." };
  }

  const protocol = parsed.protocol.toLowerCase();
  const allowInsecure = options?.allowInsecureHttp === true;
  if (!allowInsecure && protocol !== "https:") {
    return { ok: false, error: "BC API endpoint must use HTTPS." };
  }
  if (protocol !== "https:" && protocol !== "http:") {
    return { ok: false, error: "BC API endpoint protocol is not supported." };
  }
  if (parsed.host.includes("@")) {
    return { ok: false, error: "BC API endpoint must not include credentials." };
  }

  const host = parsed.host.toLowerCase();
  const allowedHostsRaw = (options?.allowedHosts ?? [])
    .map(normalizeAllowedHostEntry)
    .filter((item) => item.length > 0);
  const effectiveAllowList = allowedHostsRaw.length > 0 ? allowedHostsRaw : DEFAULT_ALLOWED_HOSTS;
  const allowed = effectiveAllowList.some((entry) => hostMatchesAllowed(host, entry));
  if (!allowed) {
    return {
      ok: false,
      error: `BC API endpoint host '${host}' is not in the allowed host list.`,
    };
  }

  return {
    ok: true,
    host,
    normalizedUrl: `${parsed.protocol}//${parsed.host}${parsed.port ? `:${parsed.port}` : ""}${parsed.remainder}`.replace(/\/$/, ""),
  };
}
